import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Button } from './ui/button';
import { ShoppingCart, User, Menu, X, Search, Phone, Heart, Package } from 'lucide-react';

export const Header = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <header className="bg-gradient-to-r from-orange-600 via-orange-500 to-yellow-500 text-white shadow-lg sticky top-0 z-50">
      <div className="bg-orange-800 text-orange-100 py-1 text-sm">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <a href="tel:+919831072381" className="flex items-center gap-2 hover:text-white"><Phone size={14} /><span>+91 9831072381</span></a>
          <div className="hidden sm:block">Authentic Puja Samagri · Howrah · Pan-India</div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-3xl">🙏</span>
            <div>
              <h1 className="text-xl font-bold tracking-wide">Sanatanbhavan</h1>
              <p className="text-xs text-orange-100 -mt-1">Puja Samagri Store</p>
            </div>
          </Link>
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search puja items..." className="w-full px-4 py-2 pl-10 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300" />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
          </form>
          <nav className="hidden md:flex items-center gap-5">
            <Link to="/products" className="hover:text-orange-200 transition">Products</Link>
            <Link to="/track-order" className="hover:text-orange-200 transition flex items-center gap-1" data-testid="track-order-link"><Package size={16} />Track Order</Link>
            <Link to="/contact" className="hover:text-orange-200 transition">Contact</Link>
            <Link to="/wishlist" className="relative" data-testid="wishlist-link">
              <Heart size={22} />
              {wishlistCount > 0 && <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{wishlistCount}</span>}
            </Link>
            <Link to="/cart" className="relative" data-testid="cart-link">
              <ShoppingCart size={24} />
              {cartCount > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>}
            </Link>
            {user ? (
              <div className="flex items-center gap-3">
                {user.role === 'admin' && <Link to="/admin" className="hover:text-orange-200">Admin</Link>}
                <Link to="/account" className="flex items-center gap-1 hover:text-orange-200"><User size={20} /><span>{user.name.split(' ')[0]}</span></Link>
                <Button variant="outline" size="sm" onClick={logout} className="text-orange-600 border-white hover:bg-white/20">Logout</Button>
              </div>
            ) : (
              <Link to="/login"><Button variant="outline" size="sm" className="text-orange-600 border-white hover:bg-white/20">Login</Button></Link>
            )}
          </nav>
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}</button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden bg-orange-700 px-4 py-4 space-y-3">
          <form onSubmit={handleSearch}><input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full px-4 py-2 rounded-full text-gray-800" /></form>
          <Link to="/products" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Products</Link>
          <Link to="/track-order" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Track Order</Link>
          <Link to="/wishlist" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Wishlist ({wishlistCount})</Link>
          <Link to="/cart" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Cart ({cartCount})</Link>
          <Link to="/contact" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
          {user ? (
            <>
              <Link to="/account" className="block py-2" onClick={() => setMobileMenuOpen(false)}>My Account</Link>
              <Link to="/orders" className="block py-2" onClick={() => setMobileMenuOpen(false)}>My Orders</Link>
              {user.role === 'admin' && <Link to="/admin" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Admin</Link>}
              <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block py-2 text-orange-200">Logout</button>
            </>
          ) : (
            <Link to="/login" className="block py-2" onClick={() => setMobileMenuOpen(false)}>Login / Register</Link>
          )}
        </div>
      )}
    </header>
  );
};
